import React, { useState, useEffect, useMemo, useRef } from 'react';

interface Template {
  id: string;
  orgId: string;
  name: string;
  description: string;
  status: string;
  version: number;
  folderId: string;
  assetId: string | null;
  blocks: Array<{ text: string; type: string }>;
  styles: string;
  metadata: {
    category: string;
    tags: string[];
    ownership: { type: string; orgId: string; userId: string };
    createdBy: string;
    createdAt: string;
    updatedAt: string;
  };
  capability: any;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  thumbnailUrl: string | null;
}

interface SelectTemplateProps {
  templates: Template[];
  categories: string[];
  selectedTemplate: number | null;
  setSelectedTemplate: (idx: number) => void;
  selectedCategory: string[];
  setSelectedCategory: (cats: string[]) => void;
  handleNext: () => void;
  searchTerm: string;
  setSearchTerm: (val: string) => void;
  isEditMode?: boolean;
}

const getTemplateCategory = (template: Template): string => {
  return template.metadata?.category || 'Uncategorized';
};

const ITEMS_PER_PAGE = 9;

const SelectTemplate: React.FC<SelectTemplateProps> = ({
  templates,
  categories,
  selectedTemplate,
  setSelectedTemplate,
  selectedCategory,
  setSelectedCategory,
  handleNext,
  searchTerm,
  setSearchTerm,
  isEditMode = false,
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1);

  // ⭐ Tracks whether we've already jumped to the selected template's page.
  const hasInitializedPage = useRef(false);

  // ⭐ Track the previous "filter signature" so we can detect real filter changes.
  const prevFilterSig = useRef<string>('');

  // ⭐ 1) Jump to the page containing the selected template once templates are loaded.
  //    We ONLY do this once, and only when templates.length > 0.
  useEffect(() => {
    if (hasInitializedPage.current) return;
    if (templates.length === 0) return;
    if (selectedTemplate === null || selectedTemplate < 0) return;

    const pageForSelected =
      Math.floor(selectedTemplate / ITEMS_PER_PAGE) + 1;
    const totalP = Math.max(
      Math.ceil(templates.length / ITEMS_PER_PAGE),
      1
    );
    const safePage = Math.min(Math.max(pageForSelected, 1), totalP);



    setCurrentPage(safePage);
    hasInitializedPage.current = true;
    // Seed the filter signature so the reset effect doesn't fire on this change.
    prevFilterSig.current = `${searchTerm}|${selectedCategory.join(',')}|${templates.length}`;
  }, [templates.length, selectedTemplate, searchTerm, selectedCategory]);

  // ⭐ 2) Reset to page 1 only when the user actually changes filters/search.
  //    We compare a "signature" and skip the first run + skip if no init yet.
  useEffect(() => {
    if (!hasInitializedPage.current) {
      // Keep the signature in sync while we're waiting to initialize.
      prevFilterSig.current = `${searchTerm}|${selectedCategory.join(',')}|${templates.length}`;
      return;
    }

    const currentSig = `${searchTerm}|${selectedCategory.join(',')}|${templates.length}`;
    if (currentSig !== prevFilterSig.current) {
      prevFilterSig.current = currentSig;
      setCurrentPage(1);
    }
  }, [searchTerm, selectedCategory, templates.length]);

  const totalPages = Math.ceil(templates.length / ITEMS_PER_PAGE);

  const paginatedTemplates = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return templates.slice(startIndex, endIndex);
  }, [templates, currentPage]);

  const indexOffset = (currentPage - 1) * ITEMS_PER_PAGE;

  // Calculate which items to show (1-indexed for display)
  const startItem = indexOffset + 1;
  const endItem = Math.min(indexOffset + ITEMS_PER_PAGE, templates.length);

  const handleCategoryChange = (cat: string) => {
    if (cat === 'All templates') {
      setSelectedCategory(['All templates']);
    } else {
      let newCategories: string[];
      if (selectedCategory.includes(cat)) {
        newCategories = selectedCategory.filter(
          (c) => c !== cat && c !== 'All templates'
        );
      } else {
        newCategories = [
          ...selectedCategory.filter((c) => c !== 'All templates'),
          cat,
        ];
      }
      setSelectedCategory(
        newCategories.length > 0 ? newCategories : ['All templates']
      );
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className='flex'>
      {/* Sidebar Filters */}
      <div className='w-1/4 pr-6 border-r'>
        <h3
          className='text-lg font-semibold text-gray-700 mb-1'
          style={{ color: '#3F5265', fontSize: '18px' }}
        >
          Category
        </h3>
        <p
          className='text-sm text-gray-500 mb-4 font-semibold'
          style={{ color: '#3B82F6', fontSize: '14px' }}
        >
          Showing {templates.length} Results
        </p>
        <div className='flex flex-col gap-2'>
          {categories.map((cat, idx) => (
            <label
              key={idx}
              className='flex items-center gap-2 text-gray-600 text-sm p-2'
              style={{ color: '#657584', fontSize: '14px' }}
            >
              <input
                type='checkbox'
                checked={selectedCategory.includes(cat)}
                onChange={() => handleCategoryChange(cat)}
              />
              {cat}
            </label>
          ))}
        </div>
      </div>

      {/* Templates */}
      <div className='w-3/4 pl-6'>
        <input
          type='text'
          placeholder='Search for a template name or category'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className='border rounded-lg p-2 w-full mb-6'
        />

        {isEditMode && (
          <div className='mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700'>
            💡 You can change the template for this campaign. The current template is preselected.
          </div>
        )}

        {templates.length === 0 ? (
          <div className='text-center py-12 text-gray-500'>
            <p>No templates found matching your criteria</p>
          </div>
        ) : (
          <>
            <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6'>
              {paginatedTemplates.map((tpl, idx) => {
                const globalIndex = indexOffset + idx;
                return (
                  <div
                    key={tpl.id || globalIndex}
                    onClick={() => setSelectedTemplate(globalIndex)}
                    className={`relative rounded-lg overflow-hidden cursor-pointer`}
                  >
                    <div
                      className={`relative rounded-lg overflow-hidden ${selectedTemplate === globalIndex
                        ? 'border-2 border-teal-500'
                        : 'border-2 border-transparent hover:border-gray-300'
                        }`}
                    >
                      <div className='w-full h-40 bg-gray-100 relative'>
                        {tpl.thumbnailUrl ? (
                          <img
                            src={tpl.thumbnailUrl}
                            alt={tpl.name}
                            className='w-full h-full object-cover'
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const parent = e.currentTarget.parentElement;
                              if (parent) {
                                const noPreviewDiv =
                                  parent.querySelector('.no-preview-text');
                                if (noPreviewDiv) {
                                  (
                                    noPreviewDiv as HTMLElement
                                  ).style.display = 'flex';
                                }
                              }
                            }}
                          />
                        ) : null}

                        {!tpl.thumbnailUrl ? (
                          <div className='w-full h-full flex items-center justify-center'>
                            <span className='text-gray-400 text-sm'>
                              {tpl.name}
                            </span>
                          </div>
                        ) : (
                          <div className='no-preview-text w-full h-full hidden items-center justify-center'>
                            <span className='text-gray-400 text-sm'>
                              {tpl.name}
                            </span>
                          </div>
                        )}
                        {selectedTemplate === globalIndex && (
                          <div className='absolute inset-0 bg-teal-500/30 flex items-center justify-center h-40 rounded-md'>
                            <span className='bg-teal-600 text-white text-xs px-3 py-1 rounded-full'>
                              Selected
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <p
                      className='text-left mt-2 text-gray-700 text-sm'
                      style={{ color: '#3F5265' }}
                    >
                      {tpl.name}
                    </p>
                    {tpl.description && (
                      <p className='text-left text-xs text-gray-500 truncate'>
                        {tpl.description}
                      </p>
                    )}
                    <p className='text-left text-xs text-teal-600 mt-1'>
                      {getTemplateCategory(tpl)}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* MODERN PAGINATION CONTROLS - matching AutomationsTable */}
            {totalPages > 1 && (
              <div className='flex items-center justify-between mt-8 text-sm text-gray-500'>
                <span>
                  Showing {startItem} to {endItem} of {templates.length} templates
                </span>

                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>

                  {(() => {
                    const pages: (number | string)[] = [];
                    const total = totalPages;
                    const current = currentPage;
                    const delta = 1;

                    pages.push(1);

                    let rangeStart = Math.max(2, current - delta);
                    let rangeEnd = Math.min(total - 1, current + delta);

                    if (rangeStart > 2) {
                      pages.push('...');
                    }

                    for (let i = rangeStart; i <= rangeEnd; i++) {
                      pages.push(i);
                    }

                    if (rangeEnd < total - 1) {
                      pages.push('...');
                    }

                    if (total > 1) {
                      pages.push(total);
                    }

                    return pages.map((page, index) => {
                      if (page === '...') {
                        return (
                          <span
                            key={`ellipsis-${index}`}
                            className="px-3 py-2 text-sm text-gray-500"
                          >
                            …
                          </span>
                        );
                      }

                      const pageNumber = page as number;
                      const isActive = pageNumber === current;

                      return (
                        <button
                          key={pageNumber}
                          onClick={() => handlePageChange(pageNumber)}
                          className={`px-4 py-2 text-sm font-medium rounded-lg ${isActive
                            ? 'bg-orange-500 text-white'
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    });
                  })()}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        <div className='flex justify-end mt-6'>
          <button
            onClick={handleNext}
            disabled={selectedTemplate === null}
            className={`px-6 py-2 rounded-md font-medium ${selectedTemplate !== null
              ? 'bg-teal-600 text-white hover:bg-teal-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            style={{ fontSize: '14px' }}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectTemplate;